import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, AppBar, Toolbar, Typography, IconButton, Tooltip, Snackbar, Alert } from '@mui/material';
import { Menu as MenuIcon, ContentCopy } from '@mui/icons-material';
import { PrimeReactProvider } from 'primereact/api';
import { CustomThemeProvider } from './contexts/ThemeContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navigation from './components/Navigation';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import RateShopping from './components/RateShopping';
import LabelManagement from './components/LabelManagement';
import LabelPrinting from './components/LabelPrinting';
import AddressValidation from './components/AddressValidation';
import Analytics from './components/Analytics';
import HealthMonitor from './components/HealthMonitor';
import TrackingWebhooks from './components/TrackingWebhooks';
import Reporting from './components/Reporting';
import './App.css';

const drawerWidth = 240;
const collapsedDrawerWidth = 80;

function AppBarWithMcpToken() {
  const { getAuthToken } = useAuth();
  const [snackbar, setSnackbar] = useState<{ open: boolean; success: boolean }>({ open: false, success: false });
  const [drawerCollapsed, setDrawerCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = (collapsed: boolean) => {
    setDrawerCollapsed(collapsed);
  };

  const handleDrawerToggleMobile = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCopyTokenForMcp = async () => {
    const token = getAuthToken();
    if (!token) {
      setSnackbar({ open: true, success: false });
      return;
    }
    try {
      await navigator.clipboard.writeText(token);
      setSnackbar({ open: true, success: true });
    } catch {
      setSnackbar({ open: true, success: false });
    }
  };

  const currentDrawerWidth = drawerCollapsed ? collapsedDrawerWidth : drawerWidth;

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        <Navigation onDrawerToggle={handleDrawerToggle} />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            minWidth: 0,
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <AppBar
            position="fixed"
            sx={{
              width: { sm: `calc(100% - ${currentDrawerWidth}px)` },
              ml: { sm: `${currentDrawerWidth}px` },
              transition: 'all 0.2s ease-in-out',
              zIndex: (theme) => theme.zIndex.drawer + 1,
            }}
          >
            <Toolbar>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggleMobile}
                sx={{ mr: 2, display: { sm: 'none' } }}
              >
                <MenuIcon />
              </IconButton>
              <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
                Veyer ODP FedEx
              </Typography>
              <Tooltip title="Copy token for MCP">
                <IconButton color="inherit" onClick={handleCopyTokenForMcp} aria-label="Copy token for MCP">
                  <ContentCopy />
                </IconButton>
              </Tooltip>
            </Toolbar>
          </AppBar>

          {/* Page Content */}
          <Box sx={{ mt: 8 }}>
            <Routes>
              <Route path="" element={<Navigate to="analytics" replace />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="label-printing" element={<LabelPrinting />} />
              <Route path="health" element={<HealthMonitor />} />
              <Route path="tracking-webhooks" element={<TrackingWebhooks />} />
              <Route path="reporting" element={<Reporting />} />
              <Route path="rate-shopping" element={<RateShopping />} />
              <Route path="labels" element={<LabelManagement />} />
              <Route path="address-validation" element={<AddressValidation />} />
            </Routes>
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.success ? 'success' : 'warning'}
        >
          {snackbar.success
            ? 'Token copied to clipboard. Use as MIDDLEWARE_AUTH_TOKEN in MCP config.'
            : 'No token available.'}
        </Alert>
      </Snackbar>
    </>
  );
}

function App() {
  return (
    <CustomThemeProvider>
      <PrimeReactProvider>
        <AuthProvider>
          <Router>
            <Routes>
              <Route path="/" element={<Navigate to="/app/analytics" replace />} />
              <Route path="/login" element={<Login />} />
              <Route path="/app/*" element={
                <ProtectedRoute>
                  <AppBarWithMcpToken />
                </ProtectedRoute>
              } />
            </Routes>
          </Router>
        </AuthProvider>
      </PrimeReactProvider>
    </CustomThemeProvider>
  );
}

export default App;
